import { Dispatch, SetStateAction, useContext } from "react";
import { defineMessages } from "react-intl";

import { ITest } from "@/modules/assessments/types/test.interface";
import { ITestAnswer } from "@/shared/components/charts/types/chartTypes";
import { BasicModal } from "@/ui-components/commonComponents/Modal/BasicModal";
import { Button } from "@/ui-components/commonComponents/Button/Button";
import { AssessmentCopy, type AssessmentCopyVariants } from "@/modules/candidate/assessmentCopy";

import { CurrentPageContext } from "../../../contexts/current-page.context";
import { getIfNextSectionNewTest } from "../../../utils/section.utils";
import {
  getIfNextQuestionNewTest,
  getIfTheEndOfTheAssessment,
  getWouldTimeoutLeadToFinish,
} from "../../../utils/candidate-assessment-view.utils";

import style from "../style.module.css";

type NavigationTarget = "nextSection" | "nextTest" | "finish";

const messages = defineMessages({
  finishContent: {
    id: "candidate.taking.assessment.timeout.finishContent",
    defaultMessage: "You've run out of time on this assessment.",
  },
  finishOk: {
    id: "candidate.taking.assessment.timeout.finishOk",
    defaultMessage: "Finish Assessment",
  },
  surveyFinishContent: {
    id: "candidate.taking.survey.timeout.finishContent",
    defaultMessage: "You've run out of time on this survey.",
  },
  surveyFinishOk: {
    id: "candidate.taking.survey.timeout.finishOk",
    defaultMessage: "Finish Survey",
  },
  nextTestContent: {
    id: "candidate.taking.timeout.nextTestContent",
    defaultMessage: "You've run out of time on this test.",
  },
  nextTestOk: {
    id: "candidate.taking.timeout.nextTestOk",
    defaultMessage: "Skip To Next Test",
  },
  nextSectionContent: {
    id: "candidate.taking.timeout.nextSectionContent",
    defaultMessage: "You've run out of time on this section.",
  },
  nextSectionOk: {
    id: "candidate.taking.timeout.nextSectionOk",
    defaultMessage: "Skip To Next Section",
  },
});

const TIMEOUT_MODAL_COPY: Record<
  NavigationTarget,
  { content: AssessmentCopyVariants; okText: AssessmentCopyVariants }
> = {
  finish: {
    content: { assessment: messages.finishContent, survey: messages.surveyFinishContent },
    okText: { assessment: messages.finishOk, survey: messages.surveyFinishOk },
  },
  nextTest: {
    content: { assessment: messages.nextTestContent, survey: messages.nextTestContent },
    okText: { assessment: messages.nextTestOk, survey: messages.nextTestOk },
  },
  nextSection: {
    content: { assessment: messages.nextSectionContent, survey: messages.nextSectionContent },
    okText: { assessment: messages.nextSectionOk, survey: messages.nextSectionOk },
  },
};

export function TimeOutModal({
  test,
  answers,
  isOpen,
  goToNextTest,
  setIsOpenModal,
  goToFinishAssessment,
  goToNextSection,
}: {
  isOpen: boolean;
  answers: ITestAnswer[];
  goToNextTest: () => void;
  goToNextSection: () => void;
  goToFinishAssessment: () => void;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  test?: ITest;
}) {
  const { questionsList, currentPage } = useContext(CurrentPageContext);

  const ifNextQuestionNewTest = getIfNextQuestionNewTest(questionsList, currentPage);
  const ifNextSectionNewTest = getIfNextSectionNewTest(questionsList, currentPage, test?.testId);
  const ifTheEndOfTheAssessment = getIfTheEndOfTheAssessment(questionsList, currentPage, test?.testId);
  const wouldLeadToFinish =
    (ifNextQuestionNewTest || ifNextSectionNewTest) &&
    getWouldTimeoutLeadToFinish({ currentPage, questionsList, answers });

  const target: NavigationTarget =
    ifTheEndOfTheAssessment || wouldLeadToFinish
      ? "finish"
      : ifNextQuestionNewTest || ifNextSectionNewTest
      ? "nextTest"
      : "nextSection";

  const modalInfo = { ...TIMEOUT_MODAL_COPY[target], target };

  const handleOk = () => {
    if (target === "finish") {
      goToFinishAssessment();
    } else if (target === "nextTest") {
      goToNextTest();
    } else {
      goToNextSection();
    }

    setIsOpenModal(false);
  };

  return (
    <BasicModal
      autoHeight
      isClosable={false}
      isModalOpen={isOpen}
      footer={
        <div className={style.actionButtons}>
          <Button onClick={handleOk}>
            <AssessmentCopy variants={modalInfo.okText} />
          </Button>
        </div>
      }
    >
      <p>
        <AssessmentCopy variants={modalInfo.content} />
      </p>
    </BasicModal>
  );
}
